import { isNil } from "lodash";
import i18next from "src/i18n/config";

type BatteryState = API.DeviceState["battery"] | null | undefined;

export enum HardwareIconColor {
    SECONDARY = "SECONDARY",
    SUCCESS = "SUCCESS",
    WARNING = "WARNING",
    ERROR = "ERROR",
}

type BatteryIcon = {
    icon: string;
    color: HardwareIconColor;
    critical: boolean;
};

const isBatteryPresent = (battery: BatteryState): boolean => {
    if (!battery || battery.present === false) {
        return false;
    } else {
        // we assume the battery is present even if the attribute is not there
        // this guarantees backward compatibility with old MDM application versions
        return true;
    }
};

export const getDeviceBatteryPresentDesc = (battery: BatteryState): string => {
    const isPresent = isBatteryPresent(battery);
    if (isPresent) {
        return i18next.t("sidepanels:devicePanel.hardwareTab.batteryStatus.present");
    } else {
        return i18next.t("sidepanels:devicePanel.hardwareTab.batteryStatus.notPresent");
    }
};

export const getDeviceBatteryPluggedDesc = (battery: BatteryState) => {
    const isPresent = isBatteryPresent(battery);
    if (!isPresent) {
        return i18next.t("common:status.unknown");
    }

    switch (battery?.plugged) {
        case 1:
            return i18next.t("sidepanels:devicePanel.hardwareTab.batteryStatus.ac");
        case 2:
            return i18next.t("sidepanels:devicePanel.hardwareTab.batteryStatus.usb");
        case 4:
            return i18next.t("sidepanels:devicePanel.hardwareTab.batteryStatus.wireless");
        default:
            return i18next.t("common:status.unknown");
    }
};

export const getDeviceBatteryStatusDesc = (battery: BatteryState) => {
    const isPresent = isBatteryPresent(battery);
    if (!isPresent) {
        return i18next.t("common:status.unknown");
    }

    switch (battery?.status) {
        case 2:
            return i18next.t("sidepanels:devicePanel.hardwareTab.batteryStatus.charging");
        case 3:
            return i18next.t("sidepanels:devicePanel.hardwareTab.batteryStatus.discharging");
        case 4:
            return i18next.t("sidepanels:devicePanel.hardwareTab.batteryStatus.notCharging");
        case 5:
            return i18next.t("sidepanels:devicePanel.hardwareTab.batteryStatus.full");
        default:
            return i18next.t("common:status.unknown");
    }
};

export const getDeviceBatteryHealthDesc = (battery: BatteryState) => {
    const isPresent = isBatteryPresent(battery);
    if (!isPresent) {
        return i18next.t("common:status.unknown");
    }

    switch (battery?.health) {
        case 2:
            return i18next.t("sidepanels:devicePanel.hardwareTab.batteryStatus.good");
        case 3:
            return i18next.t("sidepanels:devicePanel.hardwareTab.batteryStatus.overheat");
        case 4:
            return i18next.t("sidepanels:devicePanel.hardwareTab.batteryStatus.dead");
        case 5:
            return i18next.t("sidepanels:devicePanel.hardwareTab.batteryStatus.overVoltage");
        case 6:
            return i18next.t("sidepanels:devicePanel.hardwareTab.batteryStatus.failure");
        case 7:
            return i18next.t("sidepanels:devicePanel.hardwareTab.batteryStatus.cold");
        default:
            return i18next.t("common:status.unknown");
    }
};

export const getDeviceBatteryTemperatureDesc = (battery: BatteryState): string => {
    const isPresent = isBatteryPresent(battery);
    if (!isPresent) {
        return i18next.t("common:status.unknown");
    }

    const temperature = battery?.temperature;
    if (isNil(temperature) || temperature === 0) {
        return i18next.t("common:status.unknown");
    }

    return `${(temperature / 10).toFixed(1)} °C`;
};

export const getDeviceBatteryVoltageDesc = (battery: BatteryState): string => {
    const isPresent = isBatteryPresent(battery);
    if (!isPresent) {
        return i18next.t("common:status.unknown");
    }

    // Android's BatteryManager.EXTRA_VOLTAGE returns 0 when the value is unavailable
    // let's check it in addition to generic "availability" check
    const voltage = battery?.voltage;
    if (isNil(voltage) || voltage === 0) {
        return i18next.t("common:status.unknown");
    }

    return `${(voltage / 1000).toFixed(2)} V`;
};

export const getDeviceBatteryLevelDesc = (battery: BatteryState): string => {
    const isPresent = isBatteryPresent(battery);
    if (!isPresent) {
        return i18next.t("common:status.unknown");
    }

    const percentage = battery?.percentage;
    if (isNil(percentage)) {
        return i18next.t("common:status.unknown");
    }

    return `${percentage} %`;
};

export const getDeviceBatteryLevelText = (battery: BatteryState): string => {
    if (!battery) {
        return i18next.t("sidepanels:devicePanel.hardwareTab.notAvailableNotReported");
    }
    const isPresent = isBatteryPresent(battery);
    if (isPresent) {
        return i18next.t("sidepanels:devicePanel.hardwareTab.batteryStatus.levelWithStatus", {
            percentage: battery!.percentage,
            status: getDeviceBatteryStatusDesc(battery),
        });
    } else {
        return i18next.t("sidepanels:devicePanel.hardwareTab.batteryStatus.notAvailableNoBattery");
    }
};

export const getDeviceBatteryIcon = (battery: BatteryState): BatteryIcon => {
    const batteryPercentage = battery?.percentage ?? 0;
    const batteryPresent = isBatteryPresent(battery);
    if (!batteryPresent) {
        // absent battery: empty battery icon in secondary color
        return {
            icon: "fa-battery-empty",
            color: HardwareIconColor.SECONDARY,
            critical: false,
        };
    } else if (batteryPercentage >= 30) {
        // full green icon, no critical warning
        return {
            icon: "fa-battery-full",
            color: HardwareIconColor.SUCCESS,
            critical: false,
        };
    } else if (batteryPercentage >= 10) {
        // yellow half-empty battery icon, but no critical warning
        return {
            icon: "fa-battery-quarter",
            color: HardwareIconColor.WARNING,
            critical: false,
        };
    } else {
        // falling below 10% shows a red empty icon and triggers a critical warning
        return {
            icon: "fa-battery-empty",
            color: HardwareIconColor.ERROR,
            critical: true,
        };
    }
};
